import { useEffect, useState } from "react";

import { getProfileUnpublishPreview } from "../lib/profile-publish-api";
import type {
  UnpublishPreview,
  UnpublishPreviewStatus,
} from "../lib/unpublish-consequences";

export interface UseUnpublishPreviewOptions {
  getToken: () => Promise<string | null>;
  /** Fetch only while true — the dialog passes its open state so closed
   * dialogs cost nothing and reopening re-reads fresh counts. */
  enabled: boolean;
}

export interface UseUnpublishPreviewState {
  preview: UnpublishPreview | null;
  status: UnpublishPreviewStatus;
}

/**
 * useUnpublishPreview reads GET /api/profile/unpublish/preview (M11-B2,
 * plan 325) for the unpublish consequence dialog. A failure is an `error`
 * status the dialog degrades from with generic copy — the hook never turns
 * a failed read into zeros. StrictMode-safe via the cancelled flag.
 */
export function useUnpublishPreview({
  getToken,
  enabled,
}: UseUnpublishPreviewOptions): UseUnpublishPreviewState {
  const [preview, setPreview] = useState<UnpublishPreview | null>(null);
  const [status, setStatus] = useState<UnpublishPreviewStatus>("loading");

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      setStatus("loading");
      setPreview(null);
      try {
        const p = await getProfileUnpublishPreview(getToken);
        if (cancelled) return;
        setPreview(p);
        setStatus("loaded");
      } catch {
        if (cancelled) return;
        setPreview(null);
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, enabled]);

  return { preview, status };
}
